import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useBase } from "@/contexts/BaseContext";
import {
  MapPin, Activity, HeartPulse, Users, Truck, Wallet, Sprout, Loader2,
} from "lucide-react";

const METRICS = [
  { key: "horses", label: "Horses", icon: Activity },
  { key: "injured", label: "Injured", icon: HeartPulse },
  { key: "staff", label: "Staff", icon: Users },
  { key: "vehicles", label: "Vehicles", icon: Truck },
  { key: "salary_employees", label: "Payroll", icon: Wallet },
  { key: "low_feed", label: "Low feed", icon: Sprout },
];

function MetricPill({ icon: Icon, label, value, danger }) {
  return (
    <div
      className="flex items-center justify-between rounded-lg border bg-card/50 px-3 py-2"
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </span>
      <span className={`text-lg font-semibold tabular-nums ${danger && value > 0 ? "text-destructive" : "text-foreground"}`}>
        {value ?? 0}
      </span>
    </div>
  );
}

export default function BasesOverviewPage() {
  const { changeBase } = useBase();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await api.get("/bases/overview");
        if (!alive) return;
        setRows(data.bases || []);
        setTotals(data.totals || null);
      } catch (_e) {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="bases-overview-page">
      <PageHeader
        eyebrow="Operations"
        title="All Bases Overview"
        subtitle="A combined view across every base you can access. Totals sit on top; each base breaks the numbers down. Jump into a base to work inside it."
      />

      {loading ? (
        <div className="py-20 text-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : error ? (
        <div className="py-20 text-center text-muted-foreground" data-testid="overview-error">
          Couldn't load the overview. Please retry.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Grand totals */}
          {totals && (
            <Card className="rounded-xl border-[hsl(var(--ap-brass)/0.4)]" data-testid="overview-totals-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[hsl(var(--ap-saddle))]" />
                  Combined totals
                  <Badge variant="secondary" className="ml-1">{rows.length} base{rows.length === 1 ? "" : "s"}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
                  {METRICS.map((m) => (
                    <MetricPill key={m.key} icon={m.icon} label={m.label}
                      value={totals[m.key]} danger={m.key === "injured" || m.key === "low_feed"} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per-base breakdown */}
          <div className="grid gap-6 md:grid-cols-2">
            {rows.map(({ base, metrics }) => (
              <Card key={base.id} className="rounded-xl" data-testid={`overview-base-${base.code || base.id}`}>
                <CardHeader className="pb-3 flex-row items-center justify-between gap-2 space-y-0">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[hsl(var(--ap-forest))]" />
                    {base.name}
                    {base.code ? <Badge variant="outline" className="ml-1">{base.code}</Badge> : null}
                    {base.is_primary ? <Badge variant="secondary">Primary</Badge> : null}
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`overview-open-${base.code || base.id}`}
                    onClick={() => changeBase(base.id)}
                  >
                    Open base
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
                    {METRICS.map((m) => (
                      <MetricPill key={m.key} icon={m.icon} label={m.label}
                        value={metrics[m.key]} danger={m.key === "injured" || m.key === "low_feed"} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
